import { Box } from "@mui/material"
import type { FC } from "react"
import { useEffect, useMemo } from "react"
import { useFormContext, useWatch } from "react-hook-form"

import { ExistingSpecialsAlert } from "./ExistingSpecialsAlert"
import { ListPriceReadOnly } from "./ListPriceReadOnly"
import { MissingListPriceAlert } from "./MissingListPriceAlert"
import type { ListPricingFormValues } from "./schemas"
import { ShipFromSelector } from "./ShipFromSelector"

import { FormNumericField, FormSelect } from "@/components/UiKit"
import type {
  Incoterms,
  IncotermsDefinitionMap,
  Location,
  PriceChangeItem,
  ProductCompanyLocationAssociation,
} from "@/graphql/codegen/graphql"
import { useMergedIncotermsLocations } from "@/hooks/useMergedIncotermsLocations"
import { useGetIncotermsQuery } from "@/screens/Procurement/PurchaseOrder/DeliveryTermsAndShippingLegs/hooks/deliveryTerms/useGetIncotermsQuery"
import { type ExistingSpecialRow, useGetCustomerPricing } from "@/screens/Sales/hooks/useGetCustomerPricing"
import { AddressFormat } from "@/types/common"
import type { Option } from "@/types/form-fields"
import { formatLocationAddress } from "@/utils/utils"

const SHIP_FROM_GATE_FIELD = "shipFromLocationId"
const MISSING_LIST_PRICE_ERROR_TYPE = "missingListPrice"

interface ListPriceFieldsProps {
  productId: number
  customerId: string
  shipFromOptions: Option<string>[]
  productLocationsAssociations: ProductCompanyLocationAssociation[]
  onSwitchToSpecial: (special: PriceChangeItem) => void
  onMissingListPriceChange?: (isMissing: boolean) => void
}

export const ListPriceFields: FC<ListPriceFieldsProps> = ({
  productId,
  customerId,
  shipFromOptions,
  productLocationsAssociations,
  onSwitchToSpecial,
  onMissingListPriceChange,
}) => {
  const {
    control,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useFormContext<ListPricingFormValues>()
  const selectedShipFromId = watch("shipFromLocationId")
  const selectedIncoterms = useWatch({ control, name: "incoterms" }) ?? ""
  const selectedIncotermsLocationId = useWatch({ control, name: "incotermsLocationId" }) ?? ""

  const { data: incotermsData } = useGetIncotermsQuery()
  const { locations: incotermsLocations } = useMergedIncotermsLocations(productLocationsAssociations)

  const incotermsOptions = useMemo<Option<Incoterms>[]>(
    () =>
      incotermsData
        .filter((incoterm): incoterm is IncotermsDefinitionMap & { key: Incoterms } => Boolean(incoterm.key))
        .map((incoterm) => ({ label: incoterm.key, value: incoterm.key })),
    [incotermsData]
  )

  const incotermsLocationOptions = useMemo<Option<string>[]>(
    () =>
      incotermsLocations.map((location) => ({
        label: formatLocationAddress(location, AddressFormat.LocationAndAddress),
        value: location.locationId,
      })),
    [incotermsLocations]
  )

  const { listGroups, applicableSpecials, isFetching } = useGetCustomerPricing({
    productId,
    customerId,
    shipFromWarehouseAreaId: selectedShipFromId || undefined,
  })

  const isComboComplete = Boolean(selectedShipFromId && selectedIncoterms && selectedIncotermsLocationId)

  const matchingListGroup = useMemo(() => {
    if (!isComboComplete) return undefined
    return listGroups.find(
      (group) =>
        group.incoterms === selectedIncoterms &&
        (group.incotermsLocation as Location | null | undefined)?.locationId === selectedIncotermsLocationId
    )
  }, [listGroups, isComboComplete, selectedIncoterms, selectedIncotermsLocationId])

  const listTiers = useMemo(
    () =>
      (matchingListGroup?.items ?? []).flatMap((item) =>
        item.tiers.map((tier) => ({ id: tier.id, qty: tier.qty, uomId: tier.uomId, price: tier.price }))
      ),
    [matchingListGroup]
  )

  const isListPriceMissing = isComboComplete && !isFetching && !listTiers.length

  useEffect(() => {
    if (isListPriceMissing) {
      setError(SHIP_FROM_GATE_FIELD, {
        type: MISSING_LIST_PRICE_ERROR_TYPE,
        message: "No list price for this combination",
      })
      return
    }
    if (errors.shipFromLocationId?.type === MISSING_LIST_PRICE_ERROR_TYPE) {
      clearErrors(SHIP_FROM_GATE_FIELD)
    }
  }, [isListPriceMissing, setError, clearErrors, errors.shipFromLocationId?.type])

  useEffect(() => {
    onMissingListPriceChange?.(isListPriceMissing)
  }, [isListPriceMissing, onMissingListPriceChange])

  useEffect(() => {
    return () => onMissingListPriceChange?.(false)
  }, [onMissingListPriceChange])

  const narrowedSpecials = useMemo<ExistingSpecialRow[]>(() => {
    if (!isComboComplete) return []
    return applicableSpecials.filter((special) => {
      if ((special.incotermsLocation as Location | null | undefined)?.locationId !== selectedIncotermsLocationId) {
        return false
      }
      return special.incoterms === selectedIncoterms
    })
  }, [applicableSpecials, isComboComplete, selectedIncoterms, selectedIncotermsLocationId])

  const handleShipFromAdd = (warehouseId: string) => {
    setValue("shipFromLocationId", warehouseId, { shouldDirty: true, shouldValidate: true })
  }

  const handleShipFromRemove = () => {
    setValue("shipFromLocationId", "", { shouldDirty: true, shouldValidate: true })
  }

  return (
    <Box className='flex flex-col gap-3'>
      <FormNumericField
        control={control}
        name='estimatedAnnualVolume'
        placeholder='Estimated Annual Volume'
        data-testid={`sales.product-pricing-form.estimated-annual-volume-field-${productId}`}
      />
      <FormSelect
        control={control}
        name='incoterms'
        placeholder='Incoterms'
        required
        options={incotermsOptions}
        data-testid={`sales.product-pricing-form.incoterms-field-${productId}`}
      />
      <FormSelect
        control={control}
        name='incotermsLocationId'
        placeholder='Incoterms Location'
        required
        options={incotermsLocationOptions}
        data-testid={`sales.product-pricing-form.incoterms-location-field-${productId}`}
      />
      <ShipFromSelector
        mode='single'
        productId={productId}
        shipFromOptions={shipFromOptions}
        selectedShipFromId={selectedShipFromId}
        error={errors.shipFromLocationId}
        onAdd={handleShipFromAdd}
        onRemove={handleShipFromRemove}
      />
      <ExistingSpecialsAlert productId={productId} specials={narrowedSpecials} onSelectSpecial={onSwitchToSpecial} />

      {isListPriceMissing && <MissingListPriceAlert productId={productId} />}
      <ListPriceReadOnly productId={productId} tiers={listTiers} />
    </Box>
  )
}
